import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../auth/supabase';
import QrScanner from './QrScanner';
import DeploymentExpirationGate from './DeploymentExpirationGate';
import useTrackingStore from './useTrackingStore';
import { normalizeAssetId } from './trackingModel';
import { operationalAssets, activeDeploymentsForPerson } from './deploymentUsageModel';
import { evaluateRecommendedDeploymentGear, evaluateSelectedDeploymentGear } from './deploymentCapacityModel';
import { WorkflowAlert, WorkflowComplete } from './WorkflowFeedback';
import './tracking.css';
import './my-gear.css';

function typeForPosition(position) {
  return /\bemt\b/i.test(String(position || '')) ? 'EMTF' : 'EMPF';
}

export default function PrescribedBurnGearHandoff({ member, burnId }) {
  const { state, actions, sync } = useTrackingStore(member?.id);
  const [burn, setBurn] = useState(null);
  const [signup, setSignup] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loanerRadio, setLoanerRadio] = useState('');
  const [gateStatus, setGateStatus] = useState('idle');
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [complete, setComplete] = useState(null);

  const actorName = String(member?.full_name || '').trim();
  const deploymentType = typeForPosition(signup?.selected_position);
  const active = activeDeploymentsForPerson(state, actorName);
  const available = useMemo(() => operationalAssets(state).filter(asset => {
    if (asset.status !== 'available' || asset.condition !== 'ready') return false;
    if (asset.ownership !== 'personal') return true;
    const owner = String(asset.personalOwner || '').trim().toLowerCase();
    return owner && actorName.toLowerCase().includes(owner);
  }), [state, actorName]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setStatus('loading');
      setError('');
      const [burnResult, signupResult] = await Promise.all([
        supabase.from('prescribed_burns').select('*').eq('id', burnId).maybeSingle(),
        supabase.from('prescribed_burn_signups').select('*').eq('burn_id', burnId).eq('member_id', member.id).maybeSingle(),
      ]);
      if (cancelled) return;
      const firstError = burnResult.error || signupResult.error;
      if (firstError) { setError(firstError.message || 'Unable to load this Prescribed Burn assignment.'); setStatus('error'); return; }
      if (!burnResult.data || !signupResult.data) { setError('This Prescribed Burn assignment is not available to your account.'); setStatus('error'); return; }
      if (signupResult.data.status !== 'selected') { setError('Only a finalized selected Prescribed Burn assignment can be prepared in My Gear.'); setStatus('error'); return; }
      setBurn(burnResult.data);
      setSignup(signupResult.data);
      setStatus('ready');
    }
    if (burnId && member?.id && member?.member_kind === 'line_medic') load();
    else { setError('Prescribed Burn deployment handoff is available only to individual Line Medic members.'); setStatus('error'); }
    return () => { cancelled = true; };
  }, [burnId, member]);

  const addAsset = raw => {
    setError('');
    const id = normalizeAssetId(raw);
    const asset = available.find(item => normalizeAssetId(item.id) === id);
    if (!asset) return setError(`Asset ${id} is not available and ready for you.`);
    setSelected(current => current.includes(id) ? current : [...current, id]);
  };

  const removeAsset = id => setSelected(current => current.filter(item => item !== normalizeAssetId(id)));

  const submit = async event => {
    event.preventDefault();
    setError('');
    try {
      if (!burn || !signup) throw new Error('Prescribed Burn assignment is not loaded.');
      if (active.length) throw new Error('Return your current deployment before starting this Prescribed Burn assignment.');
      if (!selected.length) throw new Error('Scan each physical gear asset you are taking.');
      if (gateStatus === 'checking') throw new Error('Checking selected gear for expiring inventory.');
      if (gateStatus === 'blocked') throw new Error('Replace expiring inventory before deployment.');

      const required = evaluateSelectedDeploymentGear(state.assets, selected, deploymentType);
      if (!required.ready) throw new Error(`Missing required ${deploymentType} gear: ${required.missingLabels.join(', ')}.`);
      const recommended = evaluateRecommendedDeploymentGear(state.assets, selected);
      if (recommended.missingLabels.length && typeof window.confirm === 'function') {
        const ok = window.confirm(`Optional gear not selected:\n\n${recommended.missingLabels.map(label => `• ${label}`).join('\n')}\n\nStart the Prescribed Burn deployment without it?`);
        if (!ok) return;
      }

      actions.checkout({
        person: actorName,
        incident: burn.name,
        incidentSource: 'Prescribed Burn',
        deploymentType,
        startDate: burn.burn_date,
        loanerRadio,
        assetIds: selected,
        componentSelections: {},
        actor: actorName,
        deploymentSource: 'prescribed_burn',
        deploymentSourceId: burn.id,
        deploymentSourcePosition: signup.selected_position || '',
        deploymentSourceLocation: burn.location || '',
      });

      const { error: transitionError } = await supabase.rpc('member_set_prescribed_burn_signup', {
        target_burn_id: burn.id,
        action: 'deployed',
      });
      if (transitionError) {
        setComplete({ warning: 'Gear checkout succeeded, but the Prescribed Burn roster status did not update. Program Admin can reconcile it without changing the deployment.', burn });
        return;
      }
      setComplete({ burn });
    } catch (submitError) {
      setError(submitError?.message || 'Unable to start this Prescribed Burn deployment.');
    }
  };

  if (complete) {
    return <div className="tracking-root"><main className="tracking-page"><WorkflowComplete eyebrow="Prescribed Burn deployment started" title={`${complete.burn.name} is now in My Gear`} summary={complete.warning || 'Your selected Prescribed Burn assignment and scanned gear are now linked in My Gear.'} details={[{ label: 'Position', value: signup?.selected_position || deploymentType }, { label: 'Date', value: burn?.burn_date || '' }, { label: 'Gear', value: `${selected.length} assets` }]} primaryLabel="Open My Gear" onPrimary={() => window.location.assign('/my-gear')} /></main></div>;
  }

  return (
    <div className="tracking-root">
      <header className="tracking-topbar"><div><h1>Prepare Prescribed Burn Gear</h1><p>{actorName} · selected assignment → My Gear</p></div><div className="tracking-member-actions"><div className="tracking-offline-badge">{sync.status === 'synced' ? 'Synced' : 'Syncing'}</div><button type="button" className="tracking-signout" onClick={() => window.location.assign(`/prescribed-burns/${burnId}`)}>Back to Burn</button></div></header>
      <main className="tracking-page">
        <section className="tracking-card">
          {status === 'loading' && <div className="tracking-empty">Loading Prescribed Burn assignment…</div>}
          {burn && signup && <>
            <div className="tracking-section-head"><div><span className="my-gear-eyebrow">SELECTED PRESCRIBED BURN</span><h2>{burn.name}</h2><p>{burn.burn_date}{burn.start_time ? ` · ${String(burn.start_time).slice(0,5)}` : ''}{burn.location ? ` · ${burn.location}` : ''}</p></div></div>
            <div className="tracking-identity-note"><strong>Assigned position:</strong> {signup.selected_position || 'Line Medic'} · gear profile {deploymentType}</div>
            <form className="tracking-form" onSubmit={submit}>
              <label>Loaner radio ID <span className="tracking-optional">optional</span><input value={loanerRadio} onChange={event => setLoanerRadio(event.target.value)} placeholder="KNG 17" /></label>
              <div className="tracking-gear-block"><h3>Scan physical gear</h3><QrScanner onScan={addAsset} label="Scan gear QR / NFC ID" />
                {selected.length > 0 && <div className="my-gear-selected-list">{selected.map(id => { const asset = available.find(item => normalizeAssetId(item.id) === id); return <div key={id} className="tracking-row"><div><strong>{asset?.name || id}</strong><small>{id}</small></div><button type="button" className="tracking-link-btn" onClick={() => removeAsset(id)}>Remove</button></div>; })}</div>}
                <details className="my-gear-manual-fallback"><summary>Can't scan? Choose available gear manually</summary><div className="tracking-asset-chips">{available.map(asset => { const id = normalizeAssetId(asset.id); const chosen = selected.includes(id); return <button type="button" key={id} className={`tracking-asset-chip${chosen ? ' selected' : ''}`} onClick={() => chosen ? removeAsset(id) : addAsset(id)}><strong>{asset.name}</strong><small>{id}</small></button>; })}</div></details>
              </div>
              <DeploymentExpirationGate assetIds={selected} onStatusChange={setGateStatus} />
              <button type="submit" className="tracking-btn tracking-btn-primary" disabled={status !== 'ready' || ['checking','blocked'].includes(gateStatus)}>{gateStatus === 'checking' ? 'Checking inventory…' : gateStatus === 'blocked' ? 'Replace expiring inventory first' : 'Start Prescribed Burn Deployment'}</button>
            </form>
          </>}
          <WorkflowAlert message={error} title="Prescribed Burn handoff couldn't continue" onClose={() => setError('')} />
        </section>
      </main>
    </div>
  );
}
